import { FC, useMemo } from 'react';
import type { ChartBindingValue, CustomChartDataResponse, IndicatorSelectHandler } from '@/entities/dashboards';
import type { QualityZoneRanges } from '@/shared/lib';
import type { ChartAppearance } from './chartElements';
import { chartColumnType, withBarOrder, withShownLabels } from './chartValues';
import { BarChartView } from './views/BarChartView';
import { MetricChartView } from './views/MetricChartView';
import { ConversationListChartView } from './views/ConversationListChartView';
import { GroupedTableChartView } from './views/GroupedTableChartView';
import { HeatmapChartView } from './views/HeatmapChartView';
import { LineChartView } from './views/LineChartView';
import { PieChartView } from './views/PieChartView';
import { SelectionBreakdownChartView } from './views/SelectionBreakdownChartView';
import { TableChartView } from './views/TableChartView';
import { TextSummaryChartView } from './views/TextSummaryChartView';

type Props = {
  /** Диаграмма, чьи данные показаны: по ней же ведутся личный вид таблицы и выбранный элемент */
  chartId: string;
  title: string;
  data: CustomChartDataResponse;
  /** Пороги зон качества пользователя: по ним окрашиваются элементы со своим потолком */
  zoneRanges: QualityZoneRanges;
  /** Оформление диаграммы: направление элементов, цвета категорий и правила раскраски */
  appearance?: ChartAppearance;
  /** Высота диаграммы задана пользователем — содержимое укладывается в неё, а не в свой размер */
  hasFixedHeight?: boolean;
  /** Переход к диалогам выбранного элемента показателя; не задан — переходов у содержимого нет */
  onSelect?: IndicatorSelectHandler;
  /** Открытие одного диалога; не задано — переходов у содержимого нет */
  onOpenConversation?: (conversationId: number, openInNewTab: boolean) => void;
  /**
   * Выбор элемента для разбора связанными диаграммами; задан только у ведущей диаграммы — и тогда
   * выбор занимает собой переход к диалогам этого элемента.
   */
  onSelectElement?: (value: ChartBindingValue) => void;
  /** Выбранный в этой диаграмме элемент — он подсвечен, прочие приглушены */
  selectedElement?: ChartBindingValue;
};

/**
 * Отрисовка данных диаграммы по её типу.
 *
 * Тип диаграммы и форма данных связаны контрактом наполнения, поэтому вид выбирается по типу
 * ответа, а не по тому, какие поля нашлись в данных.
 *
 * Подписи-даты приводятся к виду, принятому для пользователя, до отрисовки: тип подписи известен
 * по колонке выборки, из которой она собрана, и знать о нём каждому виду диаграммы незачем. Порядок
 * элементов столбчатой диаграммы наводится ещё раньше — пока даты записаны машинно и сравнимы.
 *
 * Тип величины, показанной рядом с названием, берётся оттуда же и передаётся виду: величину такой
 * вид показывает сам, а ряд для точности времени у него свой.
 *
 * Переходы к диалогам приходят снаружи и бывают погашены — тогда содержимое их не выполняет и
 * нажимаемым не показано: так диаграмма показана в режиме правки состава дашборда.
 */
export const CustomChartView: FC<Props> = ({
  chartId,
  title,
  data,
  zoneRanges,
  appearance,
  hasFixedHeight,
  onSelect,
  onOpenConversation,
  onSelectElement,
  selectedElement,
}) => {
  const barOrder = appearance?.barOrder;
  const shown = useMemo(() => withShownLabels(withBarOrder(data, barOrder)), [data, barOrder]);

  switch (shown.chartType) {
    case 'metric':
      return (
        <MetricChartView title={title} payload={shown.payload} textSize={appearance?.textSize} onSelect={onSelect} />
      );
    case 'text-summary':
      return <TextSummaryChartView title={title} payload={shown.payload} textSize={appearance?.textSize} />;
    case 'heatmap':
      return (
        <HeatmapChartView
          chartId={chartId}
          title={title}
          payload={shown.payload}
          zoneRanges={zoneRanges}
          appearance={appearance}
          onSelect={onSelect}
          hasFixedHeight={hasFixedHeight}
        />
      );
    case 'table':
      return (
        <TableChartView
          chartId={chartId}
          title={title}
          payload={shown.payload}
          appearance={appearance}
          hasFixedHeight={hasFixedHeight}
          onOpenConversation={onOpenConversation}
          onSelectElement={onSelectElement}
        />
      );
    case 'grouped-table':
      return (
        <GroupedTableChartView
          chartId={chartId}
          title={title}
          payload={shown.payload}
          ruleColumns={shown.columns}
          appearance={appearance}
          hasFixedHeight={hasFixedHeight}
          onOpenConversation={onOpenConversation}
          onSelectElement={onSelectElement}
        />
      );
    case 'bar':
      return (
        <BarChartView
          title={title}
          points={shown.payload.points}
          zoneRanges={zoneRanges}
          appearance={appearance}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
        />
      );
    case 'pie':
      return (
        <PieChartView
          title={title}
          points={shown.payload.points}
          appearance={appearance}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
        />
      );
    case 'line':
      return (
        <LineChartView
          title={title}
          points={shown.payload.points}
          zoneRanges={zoneRanges}
          appearance={appearance}
          onSelectElement={onSelectElement}
        />
      );
    case 'conversation-list':
      return (
        <ConversationListChartView
          title={title}
          items={shown.payload.items}
          valueType={chartColumnType(shown.columns, 'value')}
          onOpenConversation={onOpenConversation}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
        />
      );
    case 'selection-breakdown':
      return (
        <SelectionBreakdownChartView
          title={title}
          items={shown.payload.items}
          valueType={chartColumnType(shown.columns, 'value')}
          zoneRanges={zoneRanges}
          appearance={appearance}
          onSelect={onSelect}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
        />
      );
  }
};
